import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PAGE_SIZES } from '@/lib/pagination';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/primitives';

/**
 * The offset pager under a server-paginated table: page summary, rows-per-page, previous/next.
 *
 * One component rather than per-page markup so the two paginated tables (History and the
 * Withdrawal queue) cannot drift — same sizes, same keyboard behaviour, same labels.
 */

/** Props for {@link Pager}. */
export interface PagerProps {
  /** Translated page summary, e.g. "Page 1 of 3 · 139 withdrawals". */
  summary: string;
  /** Zero-based current page. */
  page: number;
  /** Total pages, at least 1. */
  pages: number;
  /** Rows per page. */
  limit: number;
  /** Page change — already clamped. */
  onPageChange: (page: number) => void;
  /** Rows-per-page change; the caller also snaps back to the first page. */
  onLimitChange: (limit: number) => void;
}

/** The pager — see the module docs above. */
export function Pager({
  summary,
  page,
  pages,
  limit,
  onPageChange,
  onLimitChange,
}: PagerProps): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
      <span>{summary}</span>
      <div className="flex flex-wrap items-center gap-2">
        <span aria-hidden="true">{t('layout.pager.rowsPerPage')}</span>
        <Select
          value={String(limit)}
          onValueChange={(value) => {
            onLimitChange(Number(value));
          }}
        >
          <SelectTrigger
            aria-label={t('layout.pager.rowsPerPage')}
            className="h-8 w-20 min-w-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          disabled={page === 0}
          onClick={() => {
            onPageChange(Math.max(0, page - 1));
          }}
        >
          <ChevronLeft aria-hidden="true" />
          {t('layout.pager.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page + 1 >= pages}
          onClick={() => {
            onPageChange(page + 1);
          }}
        >
          {t('layout.pager.next')}
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
